import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, map, Subscription, take } from 'rxjs';
import { selectAuth } from 'src/app/store/auth/auth.reducers';
import { selectIsFullyAuthenticated } from 'src/app/store/app-ready.selectors';
import { AppLang, AppLocaleService } from 'src/app/shared/services/app-locale.service';
import { ProfileService } from './profile.service';

/**
 * Bridges the UI language choice to the backend `siteLang` so it follows the user across devices,
 * instead of living only in this browser's `localStorage`.
 *
 * - **READ on login:** on each fully-authenticated identity, GET the stored `siteLang` and apply it
 *   locally — the backend is the source of truth for a signed-in user. If the backend has none yet
 *   (`null`), SEED it ONCE with the current local choice so an existing user's language is captured.
 * - **WRITE on switch:** {@link select} — the ONLY backend-writing path — applies locally then PATCHes,
 *   but only while authenticated.
 *
 * The backend write lives ONLY in {@link select}, never in the read-on-login apply, so restoring the
 * stored value can never echo back into a PATCH (no guard flag needed — the two paths are distinct
 * methods). {@link AppLocaleService} stays the pure locale mechanism and is untouched: this service is
 * the only auth/HTTP-aware layer, so boot (before any session) never reaches the backend.
 *
 * The per-login read is gated exactly like {@link UserSettingStore}: on `selectIsFullyAuthenticated`
 * (authenticated AND on an `/app` route AND not a restricted enrollment token — never the raw
 * `isAuthenticated` flag, which would fire the GET under a session that can't reach `/v2/profile`), and
 * keyed on the userId so it runs once per identity (the readiness signal toggles on every `/app`
 * entry/exit, and a cross-tab identity switch keeps `isAuthenticated` true).
 */
@Injectable({ providedIn: 'root' })
export class UserLanguageService implements OnDestroy {

  private initialized = false;
  /** Which identity the applied language reflects, so the read fires once per user. Doubles as the
   *  "authenticated enough to write" signal for {@link select} — it is set only after a fully
   *  authenticated /app identity is seen, and cleared on logout. */
  private appliedForUserId?: number;
  private sub?: Subscription;

  constructor(
    private store: Store,
    private profile: ProfileService,
    private appLocale: AppLocaleService,
  ) {}

  /** Wire the per-login read. Called once from AppComponent; safe to call more than once. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.sub = combineLatest([
      this.store.select(selectIsFullyAuthenticated).pipe(distinctUntilChanged()),
      this.store.select(selectAuth).pipe(map((auth) => auth.userId), distinctUntilChanged()),
    ]).subscribe(([ready, userId]) => {
      if (!userId) {
        // Logged out: re-arm so the next login reads afresh (a different user must not inherit this one's
        // applied language beyond what their own read restores).
        this.appliedForUserId = undefined;
        return;
      }
      // Authenticated but not yet on an /app route (still on /auth, or holding the restricted enrollment
      // token): wait — the GET would hit /v2/profile with a session that can't reach it. Also skip a
      // repeat for the same identity.
      if (!ready || this.appliedForUserId === userId) return;
      this.appliedForUserId = userId;
      this.readForUser();
    });
  }

  /**
   * The user picked a language. Apply locally ALWAYS (so it works even before the login read has run),
   * and persist to the backend only when authenticated. The PATCH is optimistic: on failure the local
   * choice stays and is retried on the next switch. This is the ONLY method that writes the backend.
   */
  select(lang: AppLang): void {
    this.appLocale.setLanguage(lang);
    if (this.appliedForUserId !== undefined) {
      this.profile.setSiteLang(lang).pipe(take(1)).subscribe({ error: () => {} });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private readForUser(): void {
    // A failed read must degrade gracefully (keep the current local language), NOT bubble as an unhandled
    // observable error on every login where the endpoint 5xx's / times out — hence the explicit error arm.
    this.profile.getSiteLang().pipe(take(1)).subscribe({
      next: (lang) => {
        if (lang) {
          // The backend has a supported preference → it wins for an authenticated user.
          this.appLocale.setLanguage(lang);
        } else {
          // Unset (or a LanguageCode the FE can't apply) → seed the backend ONCE with the current local
          // choice so it is captured. A straight PATCH, not select() (which would redundantly re-apply the
          // language that is already active).
          this.profile.setSiteLang(this.appLocale.current).pipe(take(1)).subscribe({ error: () => {} });
        }
      },
      error: () => {},
    });
  }
}
